import { requestSE } from "../request";
import appStore from "@/utils/store";
const BASE_API_URL =
    import.meta.env["VITE_MEEFOD_API_URL"] ||
    "https://us-central1-meyo-230113.cloudfunctions.net/apiCaloo";

type ReferralMetrics = {
    installs?: number;
    subscribers?: number;
    [k: string]: unknown;
};

export type ReferralMetricsQuery = {
    from?: string | Date;
    to?: string | Date;
    tz?: string;
    granularity?: string;
    include?: string;
};

type CacheEntry<T> = { fetchedAt: number; data: T };

const METRICS_TTL_MS = 60 * 60 * 1000;

const FAKE_METRICS_SCHEMA_VERSION = 2;
let didClearFakeMetricsCaches = false;

export function isTruthyEnv(v: unknown): boolean {
    const s = String(v ?? "").trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes" || s === "on";
}

function clearPartnerDashboardMetricsCacheBestEffort() {
    if (didClearFakeMetricsCaches) return;
    didClearFakeMetricsCaches = true;
    try {
        // Dashboard keeps its own cache under this prefix. If a previous fake schema
        // produced incompatible shapes, the UI can keep showing empty charts until TTL.
        appStore.cleanup((k) => typeof k === "string" && k.startsWith("partnerMetricsCache:v1:"));
    } catch {
        // ignore
    }
}

function clampInt(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, Math.trunc(n)));
}

function addDays(d: Date, days: number): Date {
    const out = new Date(d);
    out.setDate(out.getDate() + days);
    return out;
}

function startOfDay(d: Date): Date {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
}

function parseDateMaybe(v: string): Date | null {
    const s = String(v || "").trim();
    if (!s) return null;
    const t = Date.parse(s);
    if (!Number.isFinite(t)) return null;
    return new Date(t);
}

function seedFromString(input: string): number {
    // Convert our hex hash into a 32-bit seed.
    const h = hashString(input);
    const n = parseInt(h.slice(0, 8), 16);
    return Number.isFinite(n) ? (n >>> 0) : 0x12345678;
}

function makeRng(seed: number) {
    // xorshift32
    let x = seed >>> 0;
    return {
        next(): number {
            x ^= x << 13;
            x ^= x >>> 17;
            x ^= x << 5;
            return (x >>> 0) / 0xffffffff;
        },
        int(min: number, max: number): number {
            const a = Math.min(min, max);
            const b = Math.max(min, max);
            const r = this.next();
            return a + Math.floor(r * (b - a + 1));
        },
    };
}

type FakeMetricsResponse = {
    totals: {
        installs: number;
        signups: number;
        activeSubscribers: number;
    };
    // IMPORTANT: dashboard expects either:
    // 1) series as an object of bucket arrays: series.installs[], series.newSubscribers[], series.cancellations[]
    // 2) OR series as an array of daily points.
    // Returning the object form avoids arrays being treated as objects by `isRecord()`.
    series: {
        installs: Array<{ bucket: string; value: number }>;
        newSubscribers: Array<{ bucket: string; value: number }>;
        cancellations: Array<{ bucket: string; value: number }>;
        renewals: Array<{ bucket: string; value: number }>;
    };
    activity: Array<{
        id: string;
        type: "install" | "newSub" | "renewal" | "cancel";
        ts: number;
        label?: string;
        meta?: Record<string, unknown>;
    }>;
    subscriptions: Array<Record<string, unknown>>;
    users: Array<Record<string, unknown>>;
    generatedAt: string;
    _fake: true;
    _fakeSchemaVersion: number;
};

function buildFakeReferralMetricsResponse(
    userId: string,
    referralId: string,
    query?: ReferralMetricsQuery,
): FakeMetricsResponse {
    const nq = normalizeQuery(query);
    const fromD = parseDateMaybe(nq.from) ?? addDays(new Date(), -30);
    const toD = parseDateMaybe(nq.to) ?? new Date();
    const from = startOfDay(fromD);
    const to = startOfDay(toD);

    const safeDays = clampInt(
        Math.ceil((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)),
        1,
        120,
    );

    const seed = seedFromString(`${userId}:${referralId}:${nq.granularity}:${nq.tz}`);
    const rng = makeRng(seed);

    const baseInstalls = rng.int(8, 35);
    const trend = rng.int(0, 8);
    const weekendDip = rng.int(2, 7);
    const spikeDay = rng.int(2, Math.max(2, safeDays - 2));

    const seriesInstalls: Array<{ bucket: string; value: number }> = [];
    const seriesNewSubscribers: Array<{ bucket: string; value: number }> = [];
    const seriesCancellations: Array<{ bucket: string; value: number }> = [];
    const seriesRenewals: Array<{ bucket: string; value: number }> = [];
    let totalInstalls = 0;
    let totalNewSubs = 0;
    let totalCancels = 0;
    let totalRenewals = 0;

    for (let i = 0; i <= safeDays; i++) {
        const day = addDays(from, i);
        const dow = day.getDay();
        const isWeekend = dow === 0 || dow === 6;

        const t = i / Math.max(1, safeDays);
        const seasonal = Math.sin(t * Math.PI * 2) * rng.int(0, 4);
        const spike = i === spikeDay ? rng.int(15, 55) : 0;

        let installs = baseInstalls + Math.round(trend * t) + Math.round(seasonal) + spike;
        if (isWeekend) installs = Math.max(0, installs - weekendDip);
        installs = clampInt(installs, 0, 250);

        const newSubs = clampInt(Math.round(installs * (0.08 + rng.next() * 0.08)), 0, installs);
        const cancels = clampInt(Math.round(newSubs * (0.04 + rng.next() * 0.10)), 0, newSubs);
        const renewals = clampInt(Math.round((newSubs - cancels) * (0.15 + rng.next() * 0.35)), 0, 999);

        totalInstalls += installs;
        totalNewSubs += newSubs;
        totalCancels += cancels;
        totalRenewals += renewals;

        const bucket = day.toISOString();
        seriesInstalls.push({ bucket, value: installs });
        seriesNewSubscribers.push({ bucket, value: newSubs });
        seriesCancellations.push({ bucket, value: cancels });
        seriesRenewals.push({ bucket, value: renewals });
    }

    // Build plausible totals (marketing-friendly but not crazy)
    const signups = clampInt(Math.round(totalInstalls * (0.55 + rng.next() * 0.25)), 0, totalInstalls);
    const activeSubscribers = clampInt(
        Math.round(totalNewSubs * (0.62 + rng.next() * 0.25) - totalCancels * (0.4 + rng.next() * 0.4)),
        0,
        Math.max(0, totalNewSubs),
    );

    const include = new Set(
        (nq.include || "")
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
    );
    const wantsAll = include.size === 0;
    const wants = (key: string) => wantsAll || include.has(key);

    // Activity / subscriptions / users
    const priceIds = [
        "price_1SQTiaHneiZN0NYTiiSdAJsx", // caloo
        "price_1QxPP4HneiZN0NYTQh5GU1CA", // oorenji
        "price_1SRsh6HneiZN0NYT9FmX3T36", // kit
    ];

    const now = Date.now();
    const activity: FakeMetricsResponse["activity"] = wants("activity")
        ? Array.from({ length: 18 }, (_, idx) => {
            const typeRoll = rng.next();
            const type: FakeMetricsResponse["activity"][number]["type"] =
                typeRoll < 0.55
                    ? "install"
                    : typeRoll < 0.72
                        ? "newSub"
                        : typeRoll < 0.90
                            ? "renewal"
                            : "cancel";

            const ts = now - rng.int(5 * 60 * 1000, safeDays * 24 * 60 * 60 * 1000);
            const priceId = priceIds[rng.int(0, priceIds.length - 1)];
            const amount = type === "install" ? 0 : rng.int(499, 4999);
            const currency = "EUR";

            return {
                id: `fake-${referralId}-${idx}`,
                type,
                ts,
                meta:
                    type === "install"
                        ? { source: rng.next() < 0.45 ? "link" : rng.next() < 0.8 ? "qr" : "organic" }
                        : {
                            priceId,
                            amount: { amount, currency },
                            source: rng.next() < 0.5 ? "link" : "qr",
                        },
            };
        }).sort((a, b) => b.ts - a.ts)
        : [];

    const subscriptions: FakeMetricsResponse["subscriptions"] = wants("subscriptions")
        ? Array.from({ length: 10 }, (_, idx) => {
            const statusRoll = rng.next();
            const status =
                statusRoll < 0.72
                    ? "active"
                    : statusRoll < 0.82
                        ? "trialing"
                        : statusRoll < 0.92
                            ? "past_due"
                            : "canceled";
            const interval = rng.next() < 0.25 ? "year" : "month";
            const source = rng.next() < 0.55 ? "link" : rng.next() < 0.85 ? "qr" : "organic";
            const startedAt = new Date(now - rng.int(3, 120) * 24 * 60 * 60 * 1000).toISOString();
            const renewAt = new Date(now + rng.int(5, 40) * 24 * 60 * 60 * 1000).toISOString();
            const domain = rng.next() < 0.5 ? "gmail.com" : rng.next() < 0.8 ? "icloud.com" : "company.com";

            return {
                id: `sub_fake_${idx}`,
                status,
                interval,
                source,
                email: `user${rng.int(10, 99)}.${rng.int(10, 99)}@${domain}`,
                startedAt,
                renewAt,
            };
        })
        : [];

    const users: FakeMetricsResponse["users"] = wants("users")
        ? Array.from({ length: 14 }, (_, idx) => {
            const statusRoll = rng.next();
            const status =
                statusRoll < 0.45
                    ? "installed"
                    : statusRoll < 0.75
                        ? "registered"
                        : "subscribed";
            const firstSeen = new Date(now - rng.int(5, 180) * 24 * 60 * 60 * 1000).toISOString();
            const lastSeen = new Date(now - rng.int(0, 10) * 24 * 60 * 60 * 1000).toISOString();
            const domain = rng.next() < 0.6 ? "gmail.com" : "outlook.com";
            return {
                id: `usr_fake_${idx}`,
                status,
                email: `client${rng.int(100, 999)}@${domain}`,
                firstSeen,
                lastSeen,
            };
        })
        : [];

    return {
        totals: {
            installs: totalInstalls,
            signups,
            activeSubscribers,
        },
        series: wants("series")
            ? {
                installs: seriesInstalls,
                newSubscribers: seriesNewSubscribers,
                cancellations: seriesCancellations,
                renewals: seriesRenewals,
            }
            : { installs: [], newSubscribers: [], cancellations: [], renewals: [] },
        activity,
        subscriptions,
        users,
        generatedAt: new Date().toISOString(),
        _fake: true,
        _fakeSchemaVersion: FAKE_METRICS_SCHEMA_VERSION,
    };
}

function hashString(input: string): string {
    // FNV-1a 32-bit
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(16);
}

function toIsoOrString(v: string | Date | undefined): string {
    if (!v) return "";
    if (v instanceof Date) return v.toISOString();
    return String(v);
}

function normalizeQuery(q?: ReferralMetricsQuery) {
    const from = toIsoOrString(q?.from).trim();
    const to = toIsoOrString(q?.to).trim();
    const tz = (q?.tz ? String(q.tz) : "").trim() || "UTC";
    const granularity =
        (q?.granularity ? String(q.granularity) : "").trim() || "day";
    const include = (q?.include ? String(q.include) : "").trim();

    return { from, to, tz, granularity, include };
}

function metricsCacheKey(userId: string, referralId: string, q?: ReferralMetricsQuery) {
    const nq = normalizeQuery(q);
    const suffix = hashString(JSON.stringify(nq));
    const variant = isTruthyEnv(import.meta.env["VITE_FAKE_REFERRAL_METRICS"])
        ? `fake-v${FAKE_METRICS_SCHEMA_VERSION}`
        : "real";
    return `referrals.metrics:${variant}:${userId}:${referralId}:${suffix}`;
}

function readCache<T>(key: string): CacheEntry<T> | null {
    const v = appStore.get(key) as any;
    if (!v || typeof v !== "object") return null;
    if (typeof v.fetchedAt !== "number") return null;
    if (!("data" in v)) return null;
    return v as CacheEntry<T>;
}



/**
 * 🔹 Crea una cuenta Connect de Stripe
 */
export async function createStripeAccountSE(uid: string, referralId: string, country = "ES") {
    const response = await requestSE(uid).post(`${BASE_API_URL}/stripe/create-account`, {
        userId: uid,
        referralId,
        country,
    });
    return response.data;
}

/**
 * 🔹 Crea un enlace de onboarding de Stripe
 */
export async function createStripeAccountLinkSE(uid: string, accountId: string) {
    const response = await requestSE(uid).post(`${BASE_API_URL}/stripe/account-link`, {
        accountId,
    });
    return response.data;
}

/**
 * 🔹 Comprueba el estado de una cuenta Connect
 */
export async function checkStripeAccountSE(uid: string, accountId: string) {
    const response = await requestSE(uid).post(`${BASE_API_URL}/stripe/check-account`, {
        accountId,
    });
    return response.data;
}

/**
 * 🔹 Crea sesión de checkout con reparto de ingresos (referrals)
 */
export async function createStripeCheckoutSE(uid: string, priceId: string, referralId?: string) {
    const params = new URLSearchParams();
    params.append("userId", uid);
    params.append("priceId", priceId);
    if (referralId) params.append("referralId", referralId);

    const response = await requestSE(uid).get(`${BASE_API_URL}/stripe/checkout?${params.toString()}`);
    return response.data;
}

/**
 * 🔹 Valida una sesión de Stripe Checkout completada
 */
export async function validateStripeSessionSE(uid: string, sessionId: string) {
    const response = await requestSE(uid).get(`${BASE_API_URL}/stripe/validate?session_id=${sessionId}`);
    return response.data;
}

/**
 * 🔹 Crea sesión del portal de facturación
 */
export async function createStripeBillingPortalSE(uid: string, customerId: string) {
    const response = await requestSE(uid).post(`${BASE_API_URL}/stripe/billing-portal`, {
        customerId,
    });
    return response.data;
}
export async function createStripeDashboardPortalSE(uid: string, customerId: string) {
    const response = await requestSE(uid).post(`${BASE_API_URL}/stripe/dashboard-login`, {
        accountId: customerId,
    });
    return response.data;
}
export async function updateReferralCodeSE(userId: string, newReferralCode: string) {
    const response = await requestSE(userId).patch(`${BASE_API_URL}/referrals/update-code`, {
        userId,
        newReferralCode
    });
    return response.data;
}

export async function createProductPaymentLinkSE(userId: string, priceId: string, referralId?: string, isSubscription: boolean = true) {
    console.log('createProductPaymentLinkSE called with:', { userId, priceId, referralId, isSubscription });

    const params = new URLSearchParams({
        userId,
        priceId,
        isSubscription: isSubscription ? "true" : "false"
    });

    if (referralId) params.append("referralId", referralId);

    const response = await requestSE(userId).get(`${BASE_API_URL}/subs/create-checkout?${params.toString()}`);

    console.log('createProductPaymentLinkSE response:', response.data);
    return response.data;
}

export async function getReferralMetricsSE(userId: string, referralId: string) {
    const safeReferralId = encodeURIComponent(referralId);
    const res = await requestSE(userId).get(`${BASE_API_URL}/referrals/metrics/${safeReferralId}`);
    return res.data; // { installs, subscribers }
}

export async function setReferralIdSE(code: string, email: string) {
    const safeCode = String(code || "").trim();
    const safeEmail = String(email || "").trim();

    const res = await requestSE().post(`${BASE_API_URL}/referrals/setReferralId`, {
        code: safeCode,
        email: safeEmail,
    });

    return res.data;
}

export async function getReferralMetricsWithQuerySE(
    userId: string,
    referralId: string,
    query?: ReferralMetricsQuery,
) {
    if (isTruthyEnv(import.meta.env["VITE_FAKE_REFERRAL_METRICS"])) {
        clearPartnerDashboardMetricsCacheBestEffort();
        return buildFakeReferralMetricsResponse(userId, referralId, query) as unknown as ReferralMetrics;
    }

    const safeReferralId = encodeURIComponent(referralId);
    const q = normalizeQuery(query);

    const params = new URLSearchParams();
    if (q.from) params.set("from", q.from);
    if (q.to) params.set("to", q.to);
    if (q.tz) params.set("tz", q.tz);
    if (q.granularity) params.set("granularity", q.granularity);
    if (q.include) params.set("include", q.include);

    const url = `${BASE_API_URL}/referrals/metrics/${safeReferralId}${params.toString() ? `?${params.toString()}` : ""
        }`;
    const res = await requestSE(userId).get(url);
    return res.data as ReferralMetrics;
}

export async function getReferralMetricsCachedSE(
    userId: string,
    referralId: string,
    query?: ReferralMetricsQuery,
    opts?: { ttlMs?: number; force?: boolean },
): Promise<ReferralMetrics> {
    const ttlMs = opts?.ttlMs ?? METRICS_TTL_MS;
    const key = metricsCacheKey(userId, referralId, query);
    const cached = readCache<ReferralMetrics>(key);

    if (!opts?.force && cached && Date.now() - cached.fetchedAt < ttlMs) {
        return cached.data;
    }

    try {
        const data = (await getReferralMetricsWithQuerySE(userId, referralId, query)) as ReferralMetrics;
        appStore.set(key, { fetchedAt: Date.now(), data });
        return data;
    } catch (err) {
        // Si falla el endpoint pero tenemos cache, devolvemos lo cacheado.
        if (cached?.data) return cached.data;
        throw err;
    }
}

export function clearReferralMetricsCache(userId: string, referralId: string) {
    // Best-effort: only clears the default query variant.
    appStore.delete(metricsCacheKey(userId, referralId));
}

// ============= Partner Portal (single endpoint) =============

export type PartnerInfo = {
    id: string;
    name: string;
    logoUrl?: string;
    tagline?: string;
};

export type PortalProduct = {
    id: string;
    title: string;
    text?: string;
    image?: string;
    url: string;          // Stripe checkout URL (ready to use)
    priceLabel: string;   // e.g. "9,99 €/mes"
    interval?: "month" | "year";
    recommended?: boolean;
    badge?: string;
};

export type PortalPremium = {
    isPremium: boolean;
    productId?: string | null;
    source?: string;        // "iap" | "subs" | ...
    renewalDate?: string | null;
    billingPortalUrl?: string | null;
};

export type PortalResponse = {
    ok: boolean;
    reason?: "user_not_registered" | "invalid_code" | string;
    // User
    user?: { uid: string; email: string };
    // Assignment
    assignedNow?: boolean;
    shouldRedirect?: boolean;
    resolvedReferralId?: string;
    // Partner
    partner?: PartnerInfo;
    // Premium (from existing checkPremium / checkUserPurchases)
    premium?: PortalPremium;
    // Products with checkout URLs (from existing payment-landing)
    products?: PortalProduct[];
    // Benefits list
    benefits?: string[];
};

// ── Fake portal data (demo mode) ──

function buildFakePortalResponse(
    referralIdFromUrl: string,
    email: string,
): PortalResponse {
    const lower = email.toLowerCase();

    if (lower.includes("notfound") || lower.includes("noexist") || lower.includes("newuser")) {
        return { ok: false, reason: "user_not_registered" };
    }

    if (lower.includes("redirect") || lower.includes("otherpartner")) {
        return {
            ok: true,
            user: { uid: "demo-uid-redirect", email },
            resolvedReferralId: "demoPartner",
            shouldRedirect: true,
            assignedNow: false,
            partner: { id: "demoPartner", name: "Demo Partner Clinic" },
        };
    }

    const partnerName =
        referralIdFromUrl.charAt(0).toUpperCase() +
        referralIdFromUrl.slice(1).replace(/[-_]/g, " ") +
        " Health";

    const isActive = lower.includes("premium") || lower.includes("active");
    const isCanceled = lower.includes("canceled") || lower.includes("cancel");

    const isPremium = isActive;

    return {
        ok: true,
        user: { uid: "demo-uid-001", email },
        resolvedReferralId: referralIdFromUrl,
        shouldRedirect: false,
        assignedNow: !isPremium && !isCanceled,
        partner: {
            id: referralIdFromUrl,
            name: partnerName,
            logoUrl: "",
            tagline: "Tu salud, nuestra prioridad",
        },
        premium: {
            isPremium,
            productId: isPremium ? "caloo_year_001" : null,
            source: isPremium ? "subs" : "iap",
            renewalDate: isPremium ? new Date(Date.now() + 30 * 86400000).toISOString() : null,
            billingPortalUrl: isPremium
                ? "https://billing.stripe.com/p/login/demo"
                : null,
        },
        products: isPremium
            ? []
            : [
                {
                    id: "annual",
                    title: "Caloo Premium Anual",
                    text: "Acceso completo a todas las funciones premium durante un año.",
                    url: `https://checkout.stripe.com/demo?plan=annual&ref=${referralIdFromUrl}`,
                    priceLabel: "59,99 €/año",
                    interval: "year",
                    recommended: true,
                    badge: "Ahorra 50%",
                },
                {
                    id: "monthly",
                    title: "Caloo Premium Mensual",
                    text: "Acceso completo mes a mes, cancela cuando quieras.",
                    url: `https://checkout.stripe.com/demo?plan=monthly&ref=${referralIdFromUrl}`,
                    priceLabel: "9,99 €/mes",
                    interval: "month",
                },
            ],
        benefits: [
            "Seguimiento nutricional con IA",
            "Planes personalizados por expertos",
            "Acceso a todas las recetas premium",
        ],
    };
}

// ── Portal service call (single endpoint) ──

export async function resolvePartnerPortalSE(
    referralIdFromUrl: string,
    email: string,
): Promise<PortalResponse> {
    if (isTruthyEnv(import.meta.env["VITE_FAKE_REFERRAL_METRICS"])) {
        await new Promise((r) => setTimeout(r, 700));
        return buildFakePortalResponse(referralIdFromUrl, email);
    }

    const res = await fetch(`${BASE_API_URL}/referrals/resolve-portal`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `${import.meta.env["VITE_MEFOOD_API_TOKEN"]}`,
        },
        body: JSON.stringify({
            referralIdFromUrl: String(referralIdFromUrl).trim(),
            email: String(email).trim(),
        }),
    });

    return await res.json() as PortalResponse;
}

// ── Basic partner preview (no email) ──

export type ReferralBasicInfo = {
    ok: boolean;
    code: string;
    partnerName: string;
    partnerLogoUrl?: string;
    partnerDescription?: string;
    notFound?: boolean;
    raw?: unknown;
};

export async function getReferralBasicInfo(code: string): Promise<ReferralBasicInfo> {
    const safeCode = String(code || "").trim();
    if (!safeCode) {
        return {
            ok: false,
            code: "",
            partnerName: "",
            notFound: true,
        };
    }

    try {
        // const res = await requestSE().get(
        //     `${BASE_API_URL}/referrals/basic-info/${encodeURIComponent(safeCode)}`,
        // );

        const res = await fetch(`${BASE_API_URL}/referrals/basic-info/${encodeURIComponent(safeCode)}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `${import.meta.env["VITE_MEFOOD_API_TOKEN"]}`,
            },
        });

        const data: any = await res.json();
        const candidate: any = data?.partner || data?.referral || data;

        const partnerName = String(
            candidate?.partnerName || candidate?.name || data?.partnerName || data?.name || "",
        ).trim();
        const partnerLogoUrl = String(
            candidate?.partnerLogoUrl || candidate?.logoUrl || candidate?.logo || candidate?.image || "",
        ).trim();
        const partnerDescription = String(
            candidate?.partnerDescription || candidate?.description || candidate?.tagline || "",
        ).trim();

        const notFound =
            data?.exists === false ||
            data?.notFound === true ||
            String(data?.reason || "").toLowerCase() === "not_found";

        const ok = Boolean(
            data?.ok === true ||
            data?.success === true ||
            (!notFound && partnerName),
        );

        return {
            ok,
            code: safeCode,
            partnerName: partnerName || safeCode,
            partnerLogoUrl: partnerLogoUrl || undefined,
            partnerDescription: partnerDescription || undefined,
            notFound: notFound || undefined,
            raw: data,
        };
    } catch (err: any) {
        const status = Number(err?.response?.status);
        const notFound = status === 404;
        return {
            ok: false,
            code: safeCode,
            partnerName: safeCode,
            notFound: notFound || undefined,
        };
    }
}